// Обёртка над Яндекс.Метрикой (счётчик ecommerce/целей).
// Старый счётчик 31671701 не трогаем — он работает как есть.
// Новый счётчик 110407517 используем для целей (reachGoal) и ecommerce (dataLayer).

export const METRIKA_ID = 110407517;
export const CURRENCY = 'RUB';

function hasYm() {
    return typeof window !== 'undefined' && typeof window.ym === 'function';
}

function getDataLayer() {
    if (typeof window === 'undefined') return null;
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
}

/**
 * Отправить цель в Метрику.
 * @param {string} name - идентификатор цели
 * @param {object} [params] - доп. параметры визита
 */
export function reachGoal(name, params) {
    if (!name || !hasYm()) return;
    // Разводим hit (trackPageView) и reachGoal/pushEcommerce по разным тикам:
    // при навигации SPA сначала уходит hit, затем на следующем microtask — цель,
    // иначе Метрика может «склеить» их и потерять цель.
    Promise.resolve().then(() => {
        try {
            window.ym(METRIKA_ID, 'reachGoal', name, params || undefined);
        } catch (error) {
            console.error('[analytics] reachGoal failed:', error);
        }
    });
}


/**
 * Ручной hit для SPA-счётчика (110407517 работает в режиме defer:true).
 * Шлём только в новый счётчик — 31671701 остался без defer и отправляет hits сам.
 *
 * @param {string} [url]      - абсолютный или относительный URL текущей "страницы".
 *                              Можно передавать виртуальные адреса вида
 *                              "/chat/search", "/chat/reports" и т.п.
 * @param {string} [referrer] - предыдущий URL (для корректной карты переходов).
 */
export function trackPageView(url, referrer) {
    if (!hasYm()) return;
    try {
        const targetUrl = resolveUrl(url);
        const params = {};
        if (referrer) params.referer = resolveUrl(referrer);
        window.ym(METRIKA_ID, 'hit', targetUrl, params);
    } catch (error) {
        console.error('[analytics] trackPageView failed:', error);
    }
}

function resolveUrl(input) {
    if (typeof window === 'undefined') return String(input || '/');
    if (!input) return window.location.href;
    // Уже абсолютный URL.
    if (/^https?:\/\//i.test(input)) return input;
    const path = input.startsWith('/') ? input : `/${input}`;
    return window.location.origin + path;
}

/**
 * Отправить произвольное ecommerce-событие через dataLayer.
 * @param {'detail'|'add'|'remove'|'purchase'} action
 * @param {object} payload - тело ecommerce для указанного действия
 */
export function pushEcommerce(action, payload) {
    const dl = getDataLayer();
    if (!dl || !action || !payload) return;
    try {
        dl.push({
            ecommerce: {
                currencyCode: CURRENCY,
                [action]: payload,
            },
        });
    } catch (error) {
        console.error('[analytics] pushEcommerce failed:', error);
    }
}

/**
 * Нормализовать товар к формату ecommerce Метрики.
 */
export function toEcommerceProduct(product = {}) {
    const result = {};
    if (product.id !== undefined && product.id !== null) result.id = String(product.id);
    if (product.name) result.name = String(product.name);
    if (product.category) result.category = String(product.category);
    if (product.variant) result.variant = String(product.variant);
    if (product.price !== undefined && product.price !== null) result.price = Number(product.price);
    if (product.quantity !== undefined && product.quantity !== null) result.quantity = Number(product.quantity);
    return result;
}

/**
 * Просмотр карточки товара/тарифа.
 * @param {object|object[]} products
 */
export function ecommerceDetail(products) {
    const list = Array.isArray(products) ? products : [products];
    pushEcommerce('detail', { products: list.map(toEcommerceProduct) });
}

/**
 * Добавление товара (например, переход к оплате).
 * @param {object|object[]} products
 */
export function ecommerceAdd(products) {
    const list = Array.isArray(products) ? products : [products];
    pushEcommerce('add', { products: list.map(toEcommerceProduct) });
}

/**
 * Успешная покупка.
 * @param {object|object[]} products
 * @param {object} [action] - { id: orderId, revenue }
 */
export function ecommercePurchase(products, action = {}) {
    const list = Array.isArray(products) ? products : [products];
    const actionField = {};
    if (action.id !== undefined && action.id !== null) actionField.id = String(action.id);
    if (action.revenue !== undefined && action.revenue !== null) actionField.revenue = Number(action.revenue);
    pushEcommerce('purchase', {
        actionField,
        products: list.map(toEcommerceProduct),
    });
}

/**
 * Собрать ecommerce-товар из UnifiedOrderDTO (см. amportal/app/Services/Payment/UnifiedOrderService.php).
 * Никакого хардкода цен/названий тарифов на фронте: всё приходит с бэка,
 * включая исторические цены на уже оплаченные заказы.
 *
 * Поддерживаем chat_plan (тарифы AI-чата) — остальные типы можно расширить по мере надобности.
 * Возвращает null, если по заказу мы не знаем, как построить товар для метрики.
 */
export function orderToEcommerceProduct(order) {
    if (!order || typeof order !== 'object') return null;
    const type = order.product_type || order.productType;
    const meta = order.meta || {};
    const amountCents = Number(order.amount_cents ?? order.amountCents ?? 0);
    // Метрика хочет цену в рублях (Number). Копейки округляем до целых рублей —
    // так же, как в интерфейсе (formatOrderPrice), чтобы отчёты сходились с
    // тем, что видит пользователь.
    const priceRub = Math.round(amountCents / 100);

    if (type === 'chat_plan') {
        const code = meta.plan_code || 'unknown';
        const period = meta.billing_period === 'year' ? 'year' : 'month';
        const periodSuffix = period === 'year' ? 'yearly' : 'monthly';
        const periodLabel = period === 'year' ? 'На год' : 'Помесячно';
        const planName = meta.plan_name || code;
        return {
            id: `tariff_${code}_${periodSuffix}`,
            name: `Тариф ${planName} (${periodLabel})`,
            category: 'Подписка/Тарифы',
            price: priceRub,
            quantity: 1,
        };
    }

    return null;
}

const SENT_TXN_KEY = 'am_ec_sent_txn';
const FRESH_WINDOW_MS = 30 * 60 * 1000;
const SENT_TXN_LIMIT = 100;

function readSentTxnSet() {
    if (typeof window === 'undefined') return new Set();
    try {
        return new Set(JSON.parse(window.localStorage.getItem(SENT_TXN_KEY) || '[]'));
    } catch (error) {
        console.error('[analytics] readSentTxnSet failed:', error);
        return new Set();
    }
}

function persistSentTxnSet(set) {
    if (typeof window === 'undefined') return;
    try {
        // Ограничиваем размер, чтобы localStorage не пух годами.
        const trimmed = [...set].slice(-SENT_TXN_LIMIT);
        window.localStorage.setItem(SENT_TXN_KEY, JSON.stringify(trimmed));
    } catch (error) {
        console.error('[analytics] persistSentTxnSet failed:', error);
    }
}

/**
 * Отметить transactionId как «уже отправленный» — чтобы `ecommercePurchase` не задублился,
 * если пользователь несколько раз откроет success-страницу.
 */
export function markPurchaseSent(transactionId) {
    if (!transactionId) return;
    const set = readSentTxnSet();
    set.add(String(transactionId));
    persistSentTxnSet(set);
}

/**
 * Проверить, отправляли ли мы уже purchase по этой транзакции.
 */
export function isPurchaseSent(transactionId) {
    if (!transactionId) return false;
    return readSentTxnSet().has(String(transactionId));
}

/**
 * Дожать ecommerce-purchase после возврата с оплаты.
 *
 * Идея: у нашего PHP-webhook нет браузера пользователя, поэтому JS-счётчик
 * Метрики он вызвать не может. Вместо этого фронт при первом же заходе
 * пользователя на success-страницу спрашивает у бэка список свежих оплаченных
 * заказов и, если находит там ещё не отправленную транзакцию, пушит purchase.
 *
 * @param {(params: object) => Promise<{items: Array}>} fetchPaidOrders
 *   Функция, возвращающая последние заказы (обычно `fetchPaymentOrders`).
 *   Ожидается вызов с { status: 'paid', per_page: N }.
 * @param {object} [options]
 * @param {number} [options.freshWindowMs] сколько миллисекунд считать заказ «свежим».
 * @param {number} [options.perPage] сколько последних заказов проверять.
 * @returns {Promise<number>} количество отправленных purchase-событий.
 */
export async function flushPendingEcommercePurchases(fetchPaidOrders, options = {}) {
    if (typeof fetchPaidOrders !== 'function') return 0;
    const freshWindow = Number(options.freshWindowMs) || FRESH_WINDOW_MS;
    const perPage = Number(options.perPage) || 5;

    let response;
    try {
        response = await fetchPaidOrders({ status: 'paid', per_page: perPage });
    } catch (error) {
        console.error('[analytics] flushPendingEcommercePurchases fetch failed:', error);
        return 0;
    }

    const items = Array.isArray(response?.items) ? response.items : [];
    if (items.length === 0) return 0;

    const sent = readSentTxnSet();
    const now = Date.now();
    let flushed = 0;

    for (const order of items) {
        const transactionId = order?.transaction_id || order?.transactionId;
        if (!transactionId || sent.has(String(transactionId))) continue;

        const createdAtRaw = order?.created_at || order?.createdAt;
        const createdAtMs = createdAtRaw ? Date.parse(createdAtRaw) : NaN;
        if (Number.isNaN(createdAtMs) || now - createdAtMs > freshWindow) continue;

        const product = orderToEcommerceProduct(order);
        if (!product) continue;

        ecommercePurchase(product, { id: transactionId, revenue: product.price });
        sent.add(String(transactionId));
        flushed += 1;
    }

    if (flushed > 0) persistSentTxnSet(sent);
    return flushed;
}

export default {
    reachGoal,
    trackPageView,
    pushEcommerce,
    ecommerceDetail,
    ecommerceAdd,
    ecommercePurchase,
    toEcommerceProduct,
    orderToEcommerceProduct,
    flushPendingEcommercePurchases,
    markPurchaseSent,
    isPurchaseSent,
    METRIKA_ID,
    CURRENCY,
};
